import { z } from 'zod';
/**
 * M16 — la **grille des points d'eau** de la fiche terrain (#258) : 13 points d'eau × 6 niveaux,
 * Oui (raccordé aux EU) / Non, et la colonne « Non contrôlé ».
 *
 * ⚠️ **Une case porte un décompte, pas une coche** (fiche remplie du 18/09/2026) : l'agent écrit
 * « 2 » pour deux WC, et les colonnes `Non` et `Non contrôlé` portent aussi un chiffre.
 *
 * ⚠️ **Des listes de cases renseignées**, pas une grille : une case vide n'est pas écrite, et une
 * ligne ou une colonne ajoutée au référentiel ne touche à aucune donnée. Les clés sont les
 * **codes** de `vocabulary` (M17), jamais les libellés — et des chaînes, pas un `z.enum` : un code
 * que cette version ignore ferait sinon jeter le relevé entier, pas la case (M21).
 *
 * La matrice extérieure de la fiche — le **tableau des eaux pluviales** — se saisit depuis #412
 * ({@link outdoorElementSchema}) : elle remplace la carte « Eaux de pluie vers les eaux usées »,
 * qu'un relevé d'avant garde (`stormwater.ts`).
 */
const count = z.number().int().min(0);
/** Une case de la matrice intérieure : un point d'eau, un niveau, la colonne Oui ou Non. */
export const indoorFixtureSchema = z.looseObject({
    fixture: z.string(),
    location: z.string(),
    connected: z.boolean(),
    count,
});
/**
 * La colonne « Non contrôlé » : un point d'eau et son décompte, sans niveau ni Oui/Non — d'où une
 * liste à part plutôt que des `null` dans la précédente (M16).
 */
export const uncheckedFixtureSchema = z.looseObject({ fixture: z.string(), count });
/**
 * **La ligne « Autre » de la grille** (#583, M45) : un point d'eau que la liste n'a pas, sous le
 * libellé que l'agent écrit, compté par niveau en Oui ou en Non comme les autres lignes. Sans code,
 * elle n'entre pas au référentiel : d'où une clé à part plutôt qu'une case d'`indoorFixtures` sans
 * `fixture` — deux listes sans `null` valent mieux (M16). Ni « Non contrôlé », ni anomalie désignée.
 *
 * `label` se garde tel qu'il est tapé et se trime à la lecture : trimé à chaque frappe, il ne
 * laisserait pas écrire « Lave vaisselle ».
 */
export const otherFixtureSchema = z.looseObject({
    label: z.string(),
    cells: z.array(z.looseObject({ location: z.string(), connected: z.boolean(), count })),
});
/** Le nom de la ligne « Autre » quand l'agent n'a rien écrit (M21 : le décompte ne se perd pas). */
export const OTHER_FIXTURE_LABEL = 'Autre';
/** La ligne « Autre » après que son libellé a été écrit. */
export function setOtherFixtureLabel(row, label) {
    return writtenOther({ ...(row ?? { cells: [] }), label });
}
/** La ligne « Autre » après qu'un décompte y a été écrit. */
export function setOtherFixtureCount(row, cell, count) {
    const base = row ?? { label: '', cells: [] };
    return writtenOther({
        ...base,
        cells: upsert(base.cells, (kept) => kept.location === cell.location && kept.connected === cell.connected, (kept) => (count === null || count === 0 ? null : { ...kept, ...cell, count })),
    });
}
/** Une ligne « Autre » sans libellé ni décompte n'est pas écrite (M16). */
function writtenOther(row) {
    return row.label.trim() === '' && row.cells.length === 0 ? null : row;
}
/**
 * Une case du **tableau des eaux pluviales** (#412) — la matrice extérieure de la fiche papier : un
 * élément (`outdoor_element`), son exutoire (`outdoor_destination`), le nombre, et sous « Réseau
 * EU » / « Réseau unitaire » la surface en m² ou sa note (`memo` « PM », `unknown` « ? »).
 *
 * ⚠️ `effect` : sous « Réseau unitaire », ce que la règle « collecte non séparative » du client
 * valait **le jour du relevé** (#413), figé à la saisie — une règle changée ensuite ne réécrit ni la
 * conclusion ni le rapport. Des chaînes plutôt que des `z.enum`, pour la raison des codes (M21).
 */
export const outdoorElementSchema = z.looseObject({
    element: z.string(),
    destination: z.string(),
    count: count.nullish(),
    areaM2: z.number().nonnegative().nullish(),
    areaNote: z.string().nullish(),
    effect: z.string().nullish(),
});
/**
 * Les deux exutoires dont la colonne porte une surface — « Réseau EU (indiquer m²) » et « Réseau
 * Unitaire (indiquer m²) » de la fiche. Des codes du seed de référence, cités comme `MAL` l'est.
 */
export const OUTDOOR_AREA_DESTINATIONS = ['RESEAU_EU', 'RESEAU_UNITAIRE'];
const OUTDOOR_AREA_TOTAL_LABELS = {
    RESEAU_EU: 'Total EU',
    RESEAU_UNITAIRE: 'Total unitaire',
};
/**
 * Le tableau après une saisie : le nombre, la surface ou sa note, chacun de son côté — une surface
 * efface la note, une note efface la surface. Une case vidée de tout n'est pas écrite (M16).
 */
export function setOutdoorElement(cells, cell, patch) {
    return upsert(cells, (kept) => kept.element === cell.element && kept.destination === cell.destination, (kept) => {
        const next = {
            ...kept,
            ...cell,
            ...patch,
            ...(patch.areaM2 != null ? { areaNote: null } : {}),
            ...(patch.areaNote != null ? { areaM2: null } : {}),
        };
        if (next.count === 0)
            next.count = null;
        return next.count == null && next.areaM2 == null && next.areaNote == null ? null : next;
    });
}
/**
 * Le tableau des eaux pluviales tel que la tablette le montre.
 *
 * - Les **exutoires** de la fiche, sauf la colonne que le réseau public écarte : « Réseau
 *   unitaire » en séparatif, « Réseau EU » en unitaire, les deux s'il n'est pas renseigné. Une
 *   colonne écartée qui porte une case reste là (M21).
 * - Les **éléments** relevés — ou, avec `all`, toute la liste active.
 */
export function outdoorElementMatrix(survey, vocabulary, { all = false } = {}) {
    const cells = survey.outdoorElements ?? [];
    const network = survey.plot?.publicNetworkType;
    const ruledOut = network === 'separate' ? 'RESEAU_UNITAIRE' : network === 'combined' ? 'RESEAU_EU' : null;
    const destinations = headings(vocabulary, 'outdoor_destination', cells.map((cell) => cell.destination), true)
        .filter((heading) => heading.code !== ruledOut || cells.some((cell) => cell.destination === heading.code))
        .map((heading) => ({ ...heading, carriesArea: carriesArea(heading.code) }));
    const elements = headings(vocabulary, 'outdoor_element', cells.map((cell) => cell.element), all);
    return {
        destinations,
        rows: elements.map((element) => ({
            element,
            cells: destinations.map((destination) => cells.find((cell) => cell.element === element.code && cell.destination === destination.code) ?? null),
        })),
    };
}
/**
 * **Le tableau en lignes** — ce que le bureau et le rapport impriment : une ligne par case relevée,
 * par élément puis par exutoire, dans l'ordre de la fiche. Une maison en relève une poignée sur
 * 12 × 9 : la grille entière imprimerait surtout des cases vides.
 */
export function outdoorElementLines(survey, vocabulary) {
    const matrix = outdoorElementMatrix({ outdoorElements: survey.outdoorElements }, vocabulary);
    return matrix.rows.flatMap((row) => row.cells.flatMap((cell, at) => {
        const destination = matrix.destinations[at];
        if (cell === null || destination === undefined)
            return [];
        return [
            {
                element: row.element,
                destination,
                count: cell.count ?? null,
                areaM2: cell.areaM2 ?? null,
                areaNote: cell.areaNote ?? null,
            },
        ];
    }));
}
/**
 * **Les totaux m² EU et unitaire** — un par colonne à surface que le relevé cite. Une surface « PM »
 * ou « ? » n'y compte pas, une case sans surface non plus : rien ne s'invente.
 */
export function outdoorAreaTotals(survey) {
    const cells = survey.outdoorElements ?? [];
    return OUTDOOR_AREA_DESTINATIONS.filter((destination) => cells.some((cell) => cell.destination === destination)).map((destination) => ({
        destination,
        label: OUTDOOR_AREA_TOTAL_LABELS[destination],
        areaM2: cells
            .filter((cell) => cell.destination === destination && cell.areaNote == null)
            .reduce((total, cell) => total + (cell.areaM2 ?? 0), 0),
    }));
}
function carriesArea(destination) {
    return OUTDOOR_AREA_DESTINATIONS.includes(destination);
}
/** Les cases après qu'un décompte a été écrit. */
export function setIndoorFixtureCount(cells, cell, count) {
    const same = (kept) => kept.fixture === cell.fixture &&
        kept.location === cell.location &&
        kept.connected === cell.connected;
    return upsert(cells, same, (kept) => count === null || count === 0 ? null : { ...kept, ...cell, count });
}
/** La colonne « Non contrôlé » après qu'un décompte y a été écrit. */
export function setUncheckedFixtureCount(cells, fixture, count) {
    return upsert(cells, (kept) => kept.fixture === fixture, (kept) => (count === null || count === 0 ? null : { ...kept, fixture, count }));
}
/**
 * La matrice intérieure telle que la tablette, le bureau et le rapport la montrent.
 *
 * - Les **niveaux** sont toujours tous là : c'est la grille de la fiche papier.
 * - Les **points d'eau** : ceux qui portent un décompte — ou, avec `all`, toute la liste active,
 *   ce que la tablette présente à la saisie.
 */
export function indoorFixtureMatrix(survey, vocabulary, { all = false } = {}) {
    const cells = survey.indoorFixtures ?? [];
    const unchecked = survey.uncheckedFixtures ?? [];
    const otherCells = survey.otherFixture?.cells ?? [];
    const locations = headings(vocabulary, 'fixture_location', [...cells, ...otherCells].map((cell) => cell.location), true);
    const fixtures = headings(vocabulary, 'fixture', [...cells, ...unchecked].map((cell) => cell.fixture), all);
    const countOf = (fixture, location, connected) => cells.find((cell) => cell.fixture === fixture && cell.location === location && cell.connected === connected)?.count ?? null;
    const otherCountOf = (location, connected) => otherCells.find((cell) => cell.location === location && cell.connected === connected)?.count ??
        null;
    const otherLabel = survey.otherFixture?.label.trim() ?? '';
    return {
        locations,
        rows: fixtures.map((fixture) => ({
            fixture,
            unchecked: unchecked.find((cell) => cell.fixture === fixture.code)?.count ?? null,
            counts: locations.map((location) => ({
                connected: countOf(fixture.code, location.code, true),
                notConnected: countOf(fixture.code, location.code, false),
            })),
        })),
        other: all || otherCells.length > 0
            ? {
                fixture: { code: '', label: otherLabel === '' ? OTHER_FIXTURE_LABEL : otherLabel },
                unchecked: null,
                counts: locations.map((location) => ({
                    connected: otherCountOf(location.code, true),
                    notConnected: otherCountOf(location.code, false),
                })),
            }
            : null,
    };
}
/**
 * **La matrice intérieure en lignes** — ce que le bureau et le rapport lisent : une ligne par point
 * d'eau compté à un niveau, Oui et Non côte à côte, par niveau puis dans l'ordre de la fiche ; les
 * non contrôlés ensuite. La saisie, elle, garde la grille ({@link indoorFixtureMatrix}).
 */
export function indoorFixtureLines(survey, vocabulary) {
    const matrix = indoorFixtureMatrix(survey, vocabulary);
    const rows = matrix.other === null ? matrix.rows : [...matrix.rows, matrix.other];
    const counted = matrix.locations.flatMap((location, column) => rows.flatMap((row) => {
        const cell = row.counts[column];
        if (cell === undefined || (cell.connected === null && cell.notConnected === null))
            return [];
        return [{ fixture: row.fixture, location, ...cell }];
    }));
    const unchecked = matrix.rows.flatMap((row) => row.unchecked === null
        ? []
        : [{ fixture: row.fixture, location: null, unchecked: row.unchecked }]);
    return [...counted, ...unchecked];
}
/**
 * **Chaque niveau dit comme la phrase d'anomalie le dit** — « Raccorder WC n°2 du 1ᵉʳ étage… ».
 * Par code, pas par libellé : le libellé est l'en-tête de colonne de la grille (« RDC »), et un
 * complément ne se déduit pas d'un nom (« de la dépendance »). Un niveau que cette liste ignore
 * se dit entre parenthèses, par son libellé.
 */
export const FIXTURE_LOCATION_COMPLEMENTS = {
    RDC: 'du rez-de-chaussée',
    PREMIER_ETAGE: 'du 1ᵉʳ étage',
    DEUXIEME_ETAGE: 'du 2ᵉ étage',
    SOUS_SOL: 'du sous-sol',
    GARAGE: 'du garage',
    DEPENDANCE: 'de la dépendance',
};
/**
 * **Les points d'eau à désigner** : une entrée par couple (point d'eau, niveau) compté « Non », par
 * niveau puis dans l'ordre de la fiche, numérotés parmi les « Non » seuls (#415). Un point d'eau
 * compté « Oui » est raccordé : il ne porte pas d'anomalie, pas plus que « Non contrôlé » —
 * personne n'a vu où ils partent.
 */
export function fixtureChoices(survey, vocabulary) {
    const cells = notConnectedCells(survey);
    const rank = rankIn(vocabulary);
    const fixtures = headings(vocabulary, 'fixture', cells.map((cell) => cell.fixture), false);
    const locations = headings(vocabulary, 'fixture_location', cells.map((cell) => cell.location), false);
    const labelOf = (list, code) => list.find((heading) => heading.code === code)?.label ?? code;
    const counted = new Map();
    for (const cell of cells) {
        const key = `${cell.location}\u0000${cell.fixture}`;
        const kept = counted.get(key) ?? { fixture: cell.fixture, location: cell.location, count: 0 };
        counted.set(key, { ...kept, count: kept.count + cell.count });
    }
    return [...counted.values()]
        .filter(({ count }) => count > 0)
        .toSorted((a, b) => rank('fixture_location', a.location) - rank('fixture_location', b.location) ||
        rank('fixture', a.fixture) - rank('fixture', b.fixture))
        .map(({ fixture, location, count }) => ({
        // Une seule liste de points d'eau (#347) : l'origine de l'anomalie est le point d'eau.
        code: fixture,
        label: labelOf(fixtures, fixture),
        count,
        fixture: {
            code: fixture,
            location,
            where: FIXTURE_LOCATION_COMPLEMENTS[location] ?? `(${labelOf(locations, location)})`,
        },
    }));
}
/**
 * **Une anomalie désignée qui ne suit plus la grille** — « WC n°2 du 1ᵉʳ étage » relevé, puis la
 * grille ramenée à un WC « Non », son décompte déplacé à un autre niveau, ou le WC compté « Oui » :
 * le numéro se lit parmi les « Non » seuls (#415). La phrase est figée à la
 * saisie (M19) et ne se réécrit pas : on avertit, l'agent ou le valideur tranche (M21). La
 * tablette, la transmission et la fiche de validation lisent cette même règle. `null` : rien à
 * dire, ou l'anomalie ne désigne aucun point d'eau.
 */
export function fixtureDesignationWarning(anomaly, survey, vocabulary) {
    const { fixture, fixtureLocation: location, fixtureIndex: index } = anomaly;
    if (fixture == null || location == null)
        return null;
    const countedAt = (connected) => (survey.indoorFixtures ?? [])
        .filter((cell) => cell.fixture === fixture && cell.location === location && cell.connected === connected)
        .reduce((total, cell) => total + cell.count, 0);
    const notConnected = countedAt(false);
    if (notConnected > 0 && (index == null || index <= notConnected))
        return null;
    const named = fixtureNamed(fixture, location, index, vocabulary);
    const remedy = 'Retirez l’anomalie et relevez-la à nouveau, ou corrigez la grille.';
    if (notConnected > 0) {
        return `${named} : le relevé n’en compte ${notConnected === 1 ? 'qu’un' : `que ${String(notConnected)}`} non raccordé${notConnected === 1 ? '' : 's'} à ce niveau. ${remedy}`;
    }
    return countedAt(true) > 0
        ? `${named} : le relevé le compte raccordé à ce niveau, une anomalie ne vise qu’un point d’eau non raccordé. Retirez l’anomalie, ou comptez-le « Non » dans la grille.`
        : `${named} : le relevé n’en compte plus à ce niveau. ${remedy}`;
}
/** « WC n°2 du 1ᵉʳ étage » — le point d'eau tel que la phrase d'anomalie le nomme. */
function fixtureNamed(fixture, location, index, vocabulary) {
    const labelOf = (list, code) => vocabulary.find((entry) => entry.list === list && entry.code === code)?.label ?? code;
    const where = FIXTURE_LOCATION_COMPLEMENTS[location] ?? `(${labelOf('fixture_location', location)})`;
    return [labelOf('fixture', fixture), index == null ? null : `n°${String(index)}`, where]
        .filter((part) => part !== null)
        .join(' ');
}
/**
 * **Les points d'eau comptés « Non » qui attendent encore leur anomalie** — « la grille qui
 * signale » (maquette v3, 01/10/2026) : un point d'eau qui ne part pas aux EU est un défaut, et la
 * case le rappelle tant qu'aucune anomalie ne le désigne. La tablette en tire le bouton « Relever
 * l'anomalie », la transmission un avertissement (M21 : rien ne bloque).
 */
export function fixturesToFlag(survey, anomalies) {
    return notConnectedCells(survey)
        .filter((cell) => cell.count > 0)
        .map((cell) => {
        const designating = anomalies.filter((anomaly) => anomaly.fixture === cell.fixture && anomaly.fixtureLocation === cell.location);
        return {
            fixture: cell.fixture,
            location: cell.location,
            notConnected: cell.count,
            // Sans numéro, l'anomalie désigne la case entière : une seconde aurait la même phrase, et
            // l'éditeur l'écarterait (une phrase ne se relève pas deux fois).
            flagged: designating.some((anomaly) => anomaly.fixtureIndex == null)
                ? cell.count
                : designating.length,
        };
    })
        .filter((cell) => cell.flagged < cell.notConnected);
}
/** Les cases de la colonne « Non » : les seules qu'une anomalie désigne (#415). */
function notConnectedCells(survey) {
    return (survey.indoorFixtures ?? []).filter((cell) => !cell.connected);
}
/** Le rang d'un code dans sa liste ; un code que le référentiel ignore passe après tous. */
function rankIn(vocabulary) {
    return (list, code) => vocabulary.find((entry) => entry.list === list && entry.code === code)?.sortOrder ??
        Number.MAX_SAFE_INTEGER;
}
/**
 * Les lignes ou colonnes d'une liste, dans l'ordre du référentiel : les valeurs actives quand
 * `withActive`, et toujours celles que la fiche cite — un code que le référentiel ignore en
 * dernier, montré par lui-même (M21).
 */
function headings(vocabulary, list, cited, withActive) {
    const citedCodes = new Set(cited);
    const entries = vocabulary.filter((entry) => entry.list === list);
    const known = new Set(entries.map((entry) => entry.code));
    const unknown = [...citedCodes].filter((code) => !known.has(code));
    return [
        ...entries
            .filter((entry) => (withActive && entry.active) || citedCodes.has(entry.code))
            .toSorted((a, b) => a.sortOrder - b.sortOrder)
            .map(({ code, label }) => ({ code, label })),
        ...unknown.map((code) => ({ code, label: code })),
    ];
}
/**
 * Remplace la case que `same` désigne **à sa place**, l'ajoute en fin de liste, ou la retire
 * quand `next` rend `null` — une case vide n'est pas écrite (M16). `kept` est la case d'avant :
 * ce qu'une version plus récente de la tablette y aurait rangé passe tel quel.
 */
function upsert(cells, same, next) {
    const list = cells ?? [];
    const index = list.findIndex(same);
    const written = next(index === -1 ? {} : list[index]);
    if (index === -1)
        return written === null ? [...list] : [...list, written];
    return written === null
        ? list.filter((_, at) => at !== index)
        : list.map((kept, at) => (at === index ? written : kept));
}
/** Ce que la transmission dit d'une case de {@link fixturesToFlag} (M21 : elle ne bloque pas). */
export function fixtureFlagWarning(cell, vocabulary) {
    const named = fixtureNamed(cell.fixture, cell.location, null, vocabulary);
    const counted = cell.notConnected === 1
        ? '1 compté non raccordé'
        : `${String(cell.notConnected)} comptés non raccordés`;
    const flagged = cell.flagged === 0
        ? 'aucune anomalie relevée'
        : `${String(cell.flagged)} anomalie${cell.flagged === 1 ? '' : 's'} relevée${cell.flagged === 1 ? '' : 's'}`;
    const one = cell.notConnected - cell.flagged === 1;
    const remedy = cell.flagged === 0
        ? one
            ? 'Relevez-la'
            : 'Relevez-les'
        : one
            ? 'Relevez celle qui manque'
            : 'Relevez celles qui manquent';
    return `${named} : ${counted}, ${flagged}. ${remedy} depuis la grille des points d’eau.`;
}
//# sourceMappingURL=survey-matrix.js.map